import type { FeaturedCard, FileType, LinkPayloadFile, ParsedLinkPayload, RecentFile } from '@/types'
import { featuredCards as defaultFeaturedCards } from '@/utils/data'

const STATIC_ASSET = /\.(html?|css|js|json|png|jpe?g|gif|webp|ico|svg)$/i

const TYPE_MAP: Record<string, FileType> = {
  xlsx: 'excel',
  xls: 'excel',
  docx: 'word',
  doc: 'word',
  pptx: 'powerpoint',
  ppt: 'powerpoint',
  pdf: 'pdf',
}

const OPENED_LABELS = ['Just now', '3h ago', 'Yesterday', 'Apr 25']
const FEATURED_DESCRIPTIONS = ['You frequently open this', 'You recently opened', 'You recently opened']
const DEFAULT_PREVIEW_IMAGES = defaultFeaturedCards.map((card) => card.previewImage).filter(Boolean) as string[]

function padBase64(value: string): string {
  let padded = value
  while (padded.length % 4 !== 0) {
    padded += '='
  }
  return padded
}

export function decodeBase64Token(token: string | null): string | null {
  if (!token) {
    return null
  }
  if (token.includes('@') && !/[+/=]/.test(token)) {
    return token.trim()
  }
  try {
    return atob(padBase64(token)).trim()
  } catch {
    return token.trim()
  }
}

function tryParseJson(value: string | null): Record<string, unknown> | null {
  if (!value || value.charAt(0) !== '{') {
    return null
  }
  try {
    return JSON.parse(value) as Record<string, unknown>
  } catch {
    return null
  }
}

export function getTokenFromUrl(locationObj?: Location): string | null {
  const loc = locationObj ?? window.location
  const queryToken = new URLSearchParams(loc.search).get('x')
  if (queryToken) {
    return queryToken
  }

  const segments = loc.pathname.split('/').filter(Boolean)
  if (!segments.length) {
    return null
  }

  const last = segments[segments.length - 1]
  if (STATIC_ASSET.test(last)) {
    return null
  }
  return last
}

export function parseLinkPayload(token: string | null): ParsedLinkPayload {
  if (!token) {
    return { mode: 'default', email: '', payload: null }
  }

  const decoded = decodeBase64Token(token)
  const json = tryParseJson(decoded)

  if (json && typeof json === 'object') {
    const files = Array.isArray(json.f)
      ? (json.f as LinkPayloadFile[]).filter((file) => file && typeof file === 'object')
      : []

    return {
      mode: 'payload',
      email: typeof json.e === 'string' ? json.e : '',
      payload: {
        e: typeof json.e === 'string' ? json.e : '',
        f: files,
        m: typeof json.m === 'string' ? json.m : undefined,
        p: typeof json.p === 'string' ? json.p : undefined,
      },
    }
  }

  if (decoded?.includes('@')) {
    return {
      mode: 'legacy',
      email: decoded,
      payload: null,
    }
  }

  return { mode: 'default', email: '', payload: null }
}

export function mapPayloadTypeToFileType(type: string): FileType {
  return TYPE_MAP[type.toLowerCase()] ?? 'pdf'
}

function stripExtension(name: string): string {
  const lastDot = name.lastIndexOf('.')
  return lastDot > 0 ? name.slice(0, lastDot) : name
}

function ownerFromEmail(email: string): string {
  if (!email) {
    return 'Shared with you'
  }
  const local = email.split('@')[0] ?? email
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

export function payloadFilesToRecentFiles(files: LinkPayloadFile[], email = ''): RecentFile[] {
  const owner = ownerFromEmail(email)

  return files.map((file, index) => ({
    id: `prefill-recent-${index}`,
    name: file.n || file.name || 'document',
    location: 'Shared',
    fileType: mapPayloadTypeToFileType(file.t || file.type || 'pdf'),
    opened: OPENED_LABELS[index % OPENED_LABELS.length],
    owner,
    activity: 'Shared',
  }))
}

export function payloadFilesToFeaturedCards(files: LinkPayloadFile[]): FeaturedCard[] {
  return files.slice(0, 3).map((file, index) => ({
    id: `prefill-featured-${index}`,
    name: stripExtension(file.n || file.name || 'document'),
    fileType: mapPayloadTypeToFileType(file.t || file.type || 'pdf'),
    description: FEATURED_DESCRIPTIONS[index % FEATURED_DESCRIPTIONS.length],
    timestamp: OPENED_LABELS[index % OPENED_LABELS.length],
    hasPreview: true,
    previewImage: DEFAULT_PREVIEW_IMAGES[index % DEFAULT_PREVIEW_IMAGES.length],
  }))
}

export function getPrefillFiles(parsed: ParsedLinkPayload): LinkPayloadFile[] {
  return parsed.payload?.f?.length ? parsed.payload.f : []
}
